function serialize<T>(value: T): string {
  return JSON.stringify(value, (_, nestedValue) => {
    if (nestedValue instanceof Uint8Array) {
      return {
        type: 'Uint8Array',
        data: Array.from(nestedValue),
      };
    }

    return nestedValue;
  });
}

function deserialize<T>(text: string): T {
  return JSON.parse(text, (_, nestedValue) => {
    if (nestedValue && nestedValue.type === 'Uint8Array') {
      return new Uint8Array(nestedValue.data);
    }

    return nestedValue;
  }) as T;
}

function createStorageNamespace(namespace: string = '') {
  const getNamespacedKey = (key) => namespace + key;

  const get = async <T>(key: string): Promise<T | undefined> => {
    try {
      const item = window.localStorage.getItem(getNamespacedKey(key));
      return item ? deserialize<T>(item) : undefined;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return undefined;
    }
  };

  const set = <T>(key: string, value: T) => {
    try {
      const newValue = value instanceof Function ? value(get(key)) : value;

      window.localStorage.setItem(
        getNamespacedKey(key),
        serialize<T>(newValue),
      );
    } catch (error) {
      console.warn(`Error setting localStorage key "${key}":`, error);
    }
  };

  const remove = (key: string) => {
    try {
      window.localStorage.removeItem(
        key.startsWith(namespace) ? key : getNamespacedKey(key),
      );
    } catch (error) {
      console.warn(`Error removing localStorage key "${key}":`, error);
    }
  };

  const clear = () => {
    try {
      const keys = [] as string[];
      for (let index = 0; index < window.localStorage.length; index++) {
        const key = window.localStorage.key(index);
        if (key?.startsWith(namespace)) {
          keys.push(key);
        }
      }

      keys.forEach(remove);
    } catch (error) {
      console.warn(`Error clearing localStorage "${namespace}":`, error);
    }
  };

  return {
    namespace: (newNamespace: string) =>
      createStorageNamespace(getNamespacedKey(`${newNamespace}!`)),
    get,
    batchGet: async (keys) => Promise.all(keys.map((key) => get(key))),
    set,
    batchSet: (object) => {
      Object.entries(object).forEach(([key, value]) => {
        if (value === undefined) {
          throw new Error(`cannot set ${key} to undefined`);
        }

        set(key, value);
      });
    },
    delete: remove,
    batchDelete: (keys) => keys.forEach((key) => remove(key)),
    clear,
  };
}

const createStorage = () => createStorageNamespace();

export type Storage = ReturnType<typeof createStorageNamespace>;
export default createStorage;
